import { useEffect, useState, type MouseEvent } from 'react';
import { Badge, Button, CloseButton, Group, MantineTheme, Popover, Text } from '@mantine/core';
import IonIcon from '@reacticons/ionicons';
import { NewSelectInput } from 'app/views/components/inputs/selects/NewSelectInput';
import MultiSelect from 'app/views/components/inputs/MultiSelect';
import { useForm } from '@mantine/form';
import { useDispatch, useSelector } from 'react-redux'
import { loadAllDeals } from 'app/state/ducks/deals/thunks';
import { loadEntities } from 'app/state/ducks/entities/thunks';
import { loadAllEntityProfiles } from 'app/state/ducks/entity-profile/thunks';
import { getDeals } from 'app/state/ducks/deals/selectors';
import { getEntities } from 'app/state/ducks/entities/selectors';
import { getEntityProfiles } from 'app/state/ducks/entity-profile/selectors'
import { DatePickerInput } from '@mantine/dates';
import { TextLabelInputTradeView } from 'app/views/components/inputs/TextLabelInputTradeView';
import moment from 'moment';
import { toTitleCase, getLenderEntities, getMyEntities, getAdminAgentEntities } from 'app/utils/util-functions';
import { aaStatusOptions, activityStreamOptions, holdStatusOptions, lockStatusOptions, staleDocumentsOptions, tradePaymentTypeOptions } from 'app/models/dropdown-options';
import { saveTradeFiltersToStorage, SINGLE_VALUE_FILTER_KEYS } from 'app/utils/grid-sorting-storage';
import { FilterValues } from './trade-entry'
import { MASTER_TRADE_CLOSED_LABEL, MASTER_TRADE_SETTLED_STATUS, SUB_TRADE_TERMINATED_STATUS, masterTradeStatusLabel } from './utils/master-trade-status'

type Props = {
    filterValues: FilterValues
    setFilterValues: (values: FilterValues) => void;
}

// One applied-filter chip. Multi-value filters produce one chip per selected
// value so each can be removed on its own; `selectedValue` is what that chip's x
// removes, and is absent on chips that represent the whole filter.
export type FilterChipDescriptor = {
    id: string
    label: string
    value: string
    key: keyof FilterValues
    selectedValue?: string
}

/**
 * Flattens applied filters into one chip per selected value.
 *
 * Multi-value filters previously collapsed into a single chip reading
 * "Deal: A, B +2" behind one x that cleared every selection at once. Date ranges
 * and single-value filters stay as one chip — there is only one thing to remove.
 *
 * Label resolution is injected because it reads Redux-backed entity and deal
 * lists. Exported for tests.
 */
export function buildFilterChips(
    filterValues: FilterValues | undefined,
    resolveLabel: (key: string, value: string) => string,
    formatDateRange: (key: string, value: string) => string,
): FilterChipDescriptor[] {
    const chips: FilterChipDescriptor[] = []

    for (const [key, value] of Object.entries(filterValues ?? {})) {
        if (value === undefined || value === null || value === '') continue
        if ((value as string | string[]).length === 0) continue

        const filterKey = key as keyof FilterValues
        const label = FILTER_CHIP_LABELS[filterKey] ?? toTitleCase(key)

        if (key === 'tradeDate' || key === 'settlementDate') {
            const text = formatDateRange(key, value as string)
            if (text) chips.push({ id: key, label, value: text, key: filterKey })
            continue
        }

        if (Array.isArray(value)) {
            for (const selected of value as string[]) {
                const text = resolveLabel(key, selected)
                // An entity or deal archived since the filter was saved resolves
                // to '' — skip it rather than render a chip with no value.
                if (!text) continue
                chips.push({ id: `${key}:${selected}`, label, value: text, key: filterKey, selectedValue: selected })
            }
            continue
        }

        chips.push({ id: key, label, value: resolveLabel(key, value as string), key: filterKey })
    }

    return chips
}

// Trade Status filter options. The values are the ledger statuses that
// trade-entry.tsx matches rows against, so only the terminal one's LABEL changes.
// Issue #6600 Conceal 'Draft' filter option & Create Draft Trade Button
const tradeStatusOptions = [
    /* { value: 'Draft', label: 'Draft' }, */
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Open', label: 'Open' },
    { value: MASTER_TRADE_SETTLED_STATUS, label: MASTER_TRADE_CLOSED_LABEL },
    { value: 'Canceled', label: 'Canceled' },
    // A SUB-trade status (LOS-7001): matches trades with at least one Terminated sub-trade.
    { value: SUB_TRADE_TERMINATED_STATUS, label: 'Terminated' },
]

// Chip labels that toTitleCase gets wrong: it would render these as "Aa Status"
// and "Entity Any Side".
const FILTER_CHIP_LABELS: Partial<Record<keyof FilterValues, string>> = {
    aaStatus: 'AA Status',
    entityAnySide: 'Entity (Any Side)',
}

// Filters whose VALUES are deal or entity ids. Their chips need the deal /
// entity stores to render a name, so those stores are loaded whenever one of
// these is applied, not only while the panel is open.
const LABEL_BACKED_FILTER_KEYS: ReadonlyArray<keyof FilterValues> = ['deal', 'entityAnySide', 'myEntity', 'counterparty', 'agent']

// Filters superseded by Entity (Any Side), which covers the two SIDES of a trade.
// Agent is absent on purpose: the agent is not a side, so that filter stays live and
// ANDs with this one. Kept together so the disable and the clear can't drift apart.
const SIDE_SPECIFIC_ENTITY_KEYS: ReadonlyArray<keyof FilterValues> = ['myEntity', 'counterparty']

// Every filter key reset to its empty shape. `form.setValues` merges into the
// current values rather than replacing them, so Clear All has to name each key —
// passing {} would be a no-op.
const EMPTY_FILTERS: FilterValues = {
    tradeStatus: [],
    tradeDate: [],
    settlementDate: [],
    tradeAction: [],
    deal: [],
    entityAnySide: [],
    myEntity: [],
    counterparty: [],
    agent: [],
    paymentMethod: [],
    aaStatus: [],
    unappliedTransactions: '',
    lockStatus: '',
    holdStatus: '',
    allocated: '',
    activityStream: '',
    staleDocuments: '',
    documentsNotRefreshed: '',
}

const datePickerStyles = (theme: MantineTheme) => ({
    day: {
        '&[data-in-range]:not([data-selected])': {
            backgroundColor: theme.colors.blue[1],
        },
        '&:hover:not([data-disabled]):not([data-selected])': {
            backgroundColor: theme.colors.blue[2],
        },
    },
})

function PlusButtonWithPopover({ filterValues, setFilterValues }: Props) {
    const allDeals = useSelector(getDeals)
    const allEntities = useSelector(getEntities)
    const entityProfiles = useSelector(getEntityProfiles)

    const myEntities = getMyEntities(allEntities, entityProfiles)
    const agentEntities = getAdminAgentEntities(allEntities, entityProfiles)
    const lenderEntities = getLenderEntities(allEntities, entityProfiles)

    const [isPopoverOpen, setPopoverOpen] = useState(false);
    const dispatch = useDispatch();

    // The option lists (deals, entities, profiles) are not loaded for the list
    // itself any more — GET /api/trade-list carries the names it renders. They
    // are needed in two situations, and loaded in both:
    //
    //   1. The panel is open — the five entity/deal selects list them.
    //   2. An APPLIED filter is keyed by a deal or entity id (saved filters are
    //      restored from localStorage on a fresh load). Its chip resolves the
    //      label from these stores, and `buildFilterChips` drops a chip whose
    //      label is '' — so without this the filter would be applied with no
    //      chip to show it or lift it: an invisible filter.
    //
    // A store already holding its list is not loaded again: coalescing only
    // shares a request in flight, so reopening the panel would otherwise
    // refetch every list (deals alone is three requests). An empty store —
    // never loaded, or a load that failed — is fetched on the next trigger,
    // which is the retry.
    const appliedFilterNeedsLabels = LABEL_BACKED_FILTER_KEYS.some(
        (key) => ((filterValues?.[key] as string[] | undefined) ?? []).length > 0,
    );
    const needsOptionStores = isPopoverOpen || appliedFilterNeedsLabels;
    const dealsLoaded = allDeals.length > 0
    const entitiesLoaded = allEntities.length > 0
    const profilesLoaded = entityProfiles.length > 0
    useEffect(() => {
        if (!needsOptionStores) return;
        if (!dealsLoaded) dispatch(loadAllDeals() as any);
        if (!entitiesLoaded) dispatch(loadEntities() as any);
        if (!profilesLoaded) dispatch(loadAllEntityProfiles() as any);
    }, [needsOptionStores, dealsLoaded, entitiesLoaded, profilesLoaded, dispatch]);

    // Shown in an entity/deal select while its store is still empty. Both stores
    // being empty is indistinguishable from "still loading" here; a tenant with
    // no entities at all has no trades to filter either.
    const entityOptionsHint = allEntities.length === 0 ? 'Loading entities…' : 'Nothing found';
    const dealOptionsHint = allDeals.length === 0 ? 'Loading deals…' : 'Nothing found';

    const togglePopover = () => setPopoverOpen((prev) => !prev);

    // Filter and sort approved deals
    const sortedDeals = allDeals.sort((a, b) => a.dealName.localeCompare(b.dealName));

    // Options for Entity (Any Side): everything that can appear on either SIDE of a
    // trade — the My Entity and Counterparty populations. Agents are excluded: the
    // agent is not a side, and the Agent filter covers it separately. Deduped by id,
    // since an entity can be in both populations.
    const anySideEntities = Array.from(
        new Map(
            [...myEntities, ...lenderEntities].map((entity) => [entity.id, entity]),
        ).values(),
    ).sort((a, b) => a.entityName.localeCompare(b.entityName));

    const form: any = useForm({
        initialValues: filterValues,
    })

    async function submitForm(event: { preventDefault: () => void }) {
        event.preventDefault()
        setFilterValues(form.values)
        saveTradeFiltersToStorage(form.values)
        setPopoverOpen(false)
    }

    // Clears every filter, including the 'Open' trade status the app defaults to
    // on a fresh load. Leaves the popover open so the user can pick a new set
    // straight away.
    function clearAllFilters() {
        form.setValues(EMPTY_FILTERS)
        setFilterValues(EMPTY_FILTERS)
        saveTradeFiltersToStorage(EMPTY_FILTERS)
    }

    // Entity (Any Side) supersedes My Entity and Counterparty, so those two are
    // disabled while it holds a selection — exactly the redesign spec's rule. Agent
    // is untouched: it is not a side of the trade, so it stays usable alongside.
    // The two values are cleared on the way in: a disabled control whose value is
    // still applied is an invisible filter the user cannot see or lift.
    const anySideInputProps = form.getInputProps('entityAnySide')
    const anySideSelected = (form.values.entityAnySide ?? []).length > 0

    const handleAnySideChange = (next: string[]) => {
        anySideInputProps.onChange(next)
        if (next.length > 0) {
            for (const key of SIDE_SPECIFIC_ENTITY_KEYS) form.setFieldValue(key, [])
        }
    }

    // Mantine v6's PickerInputBase calls `onClear` when the dropdown closes with a
    // partial range (start picked, no end). The default wipes the value entirely;
    // we override to commit `[start, start]` so a single-date pick survives close
    // and the filter treats it as a one-day filter. The X button does NOT go
    // through here -- see `dateRightSection`.
    const handleDateClear = (key: 'tradeDate' | 'settlementDate') => () => {
        const v = form.values[key]
        if (Array.isArray(v) && v.length === 2 && v[0] && !v[1]) {
            form.setFieldValue(key, [v[0], v[0]])
            return
        }
        form.setFieldValue(key, [])
    }

    // Right section for the date inputs. PickerInputBase resolves its own clear
    // button as `rightSection || (clearable && shouldClear ? <CloseButton/> : null)`,
    // so the chevron passed here ALWAYS wins and the built-in clear button can
    // never render -- which is why `onClear` above was dead code and a picked
    // range could not be cleared to choose another. Swap the chevron for a clear
    // button once there is a value.
    //
    // The X always wipes the value outright: `handleDateClear`'s partial-range
    // normalisation is right for a dropdown close, but the X renders as soon as a
    // start date is picked, and there it would commit `[start, start]` instead of
    // clearing.
    //
    // The input is mounted as `component='button'`, so a click inside it would
    // otherwise bubble up and re-open the calendar: preventDefault on mousedown
    // stops the focus/open, stopPropagation stops the toggle.
    const dateRightSection = (key: 'tradeDate' | 'settlementDate', hasValue: boolean) =>
        hasValue ? (
            <CloseButton
                variant="transparent"
                size="md"
                aria-label={key === 'tradeDate' ? 'Clear trade date' : 'Clear settlement date'}
                // `.customRightSection` sets `pointer-events: none` so a click on the
                // chevron falls through to the input button and opens the calendar.
                // The clear button is the one thing in here that must swallow its own
                // click, so it re-enables pointer events for itself only.
                style={{ pointerEvents: 'auto' }}
                onMouseDown={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    form.setFieldValue(key, [])
                }}
            />
        ) : (
            <IonIcon size="large" name="chevron-down-outline"></IonIcon>
        )

    // Build the input's display string. PickerInputBase renders this as the children
    // of a Mantine `Input` mounted as `component='button'` with `multiline: true`,
    // so `\n` becomes a real line break (with `white-space: pre-line` in styles).
    // Same-day ranges collapse to a single date so the input doesn't read
    // "05-May-2026 - 05-May-2026" for what is effectively a one-day pick.
    const formatRangeForInput = (v: unknown): { text: string; multiline: boolean } => {
        if (!Array.isArray(v) || v.length !== 2) return { text: '', multiline: false }
        const [s, e] = v as [Date | null, Date | null]
        if (!s && !e) return { text: '', multiline: false }
        const fmt = (d: Date) => moment(d).format('DD-MMM-YYYY')
        if (s && !e) return { text: fmt(s), multiline: false }
        if (!s && e) return { text: fmt(e), multiline: false }
        if (moment(s!).isSame(moment(e!), 'day')) return { text: fmt(s!), multiline: false }
        return { text: `${fmt(s!)}\n${fmt(e!)}`, multiline: true }
    }
    const tradeDateDisplay = formatRangeForInput(form.values.tradeDate)
    const settlementDateDisplay = formatRangeForInput(form.values.settlementDate)

    const removeFilter = (key: keyof FilterValues) => {
        const updatedFilters = { ...filterValues } as FilterValues;
        if (key === 'tradeDate' || key === 'settlementDate') {
            updatedFilters[key] = [] as unknown as string;
        } else if (SINGLE_VALUE_FILTER_KEYS.includes(key)) {
            (updatedFilters as Record<string, unknown>)[key] = '';
        } else {
            (updatedFilters as Record<string, unknown>)[key] = [];
        }
        setFilterValues(updatedFilters);
        saveTradeFiltersToStorage(updatedFilters);
    };

    // Drops one value from a multi-value filter, leaving its other selections
    // applied. Falls back to clearing the whole filter for date ranges and
    // single-value filters, which have nothing to partially remove.
    const removeFilterValue = (key: keyof FilterValues, value: string) => {
        const current = filterValues?.[key];
        if (!Array.isArray(current)) {
            removeFilter(key);
            return;
        }
        const updatedFilters = {
            ...filterValues,
            [key]: (current as string[]).filter((v) => v !== value),
        } as FilterValues;
        setFilterValues(updatedFilters);
        saveTradeFiltersToStorage(updatedFilters);
    };

    useEffect(() => {
        form.setValues(filterValues)
    }, [filterValues]);

    function singleLabel(key: string, value: string): string {
        if (key === 'deal') return allDeals.find(deal => deal.id === value)?.dealName ?? ''
        if (key === 'entityAnySide' || key === 'myEntity' || key === 'counterparty' || key === 'agent') {
            return allEntities.find(entity => entity.id === value)?.entityName ?? ''
        }
        if (key === 'paymentMethod') return tradePaymentTypeOptions.find(option => option.value === value)?.label ?? ''
        if (key === 'tradeStatus') return masterTradeStatusLabel(value)
        return value ?? ''
    }

    const formatTradeDate = (key: string, value: string) => {
        if ((key === 'tradeDate' || key === 'settlementDate') && Array.isArray(value) && value.length === 2) {
            const [startDate, endDate] = value;
            if (!startDate && !endDate) return ''
            // Partial range (one endpoint missing) — render the single date rather than
            // "Invalid date" from moment(null).
            if (startDate && !endDate) return moment(startDate).format('MM/DD/YY')
            if (!startDate && endDate) return moment(endDate).format('MM/DD/YY')
            // Same-day range (committed by onClear when user picked one date) reads as
            // a single date, not "X - X".
            if (moment(startDate).isSame(moment(endDate), 'day')) return moment(startDate).format('MM/DD/YY')
            return `${moment(startDate).format('MM/DD/YY')} - ${moment(endDate).format('MM/DD/YY')}`;
        }
        return value;
    };

    // Count active filters to determine positioning strategy
    const activeFilterCount = Object.entries(filterValues || {})
        .filter(([, value]) => value !== undefined && value !== null && value !== '' && value.length !== 0).length;

    // Use manual offset when fewer filters, auto-positioning when many filters
    const shouldUseManualOffset = activeFilterCount < 3;

    const filterChips = buildFilterChips(filterValues, singleLabel, formatTradeDate);

    return (
        <div className="container">
            {filterChips.map((chip) => (
                <Badge
                    key={chip.id}
                    classNames={{
                        root: 'customBadge',
                    }}
                    size="lg"
                    color="blue"
                    rightSection={
                        // A real <button>, not a clickable <ion-icon>: the icon this
                        // replaces had no tabindex, role or accessible name, so a
                        // filter could only be dismissed with a mouse. Matches the
                        // MultiSelect value chips, which already use CloseButton.
                        <CloseButton
                            size={20}
                            iconSize={14}
                            variant="transparent"
                            aria-label={`Remove filter ${chip.label}: ${chip.value}`}
                            sx={{ color: '#784595' }}
                            onClick={() => chip.selectedValue !== undefined
                                ? removeFilterValue(chip.key, chip.selectedValue)
                                : removeFilter(chip.key)}
                        />
                    }
                >
                    <Group>
                        <Text className='customBadgeText'>{`${chip.label}: `}</Text>
                        <Text className='customBadgeTextValue'>{chip.value}</Text>
                    </Group>
                </Badge>
            ))}
            <Popover
                opened={isPopoverOpen}
                onClose={() => { setPopoverOpen(false) }}
                position="bottom"
                shadow="md"
                middlewares={{ flip: true, shift: true, inline: true }}
            >
                <Popover.Target>
                    <Button
                        variant="outline"
                        size="md"
                        className={`plusButton ${isPopoverOpen ? 'plusButtonOpen' : 'plusButtonClosed'}`}
                        onClick={togglePopover}
                    >
                        <IonIcon
                            name="add-outline"
                            style={{
                                width: '2em',
                                height: '2em',
                                color: isPopoverOpen ? '#FFFFFF' : '#32439A',
                                fontWeight: 800,
                            }}
                        />
                    </Button>
                </Popover.Target>
                <Popover.Dropdown style={shouldUseManualOffset ? {
                    borderRadius: '12px',
                    transform: 'translateX(30%)'
                } : {
                    borderRadius: '12px'
                }}>
                    <form onSubmit={submitForm}>
                        <div className="filterMenu tradeFilterMenu">
                            <div className="filterMenuSelected">
                                <div className="filterMenuLeft">
                                    <div className="filterSection">
                                        <div className="filterSectionHeading">Entity &amp; Deal</div>
                                        <MultiSelect pinSelectedToTop wrapperType="trade" label="Entity (Any Side)" data={anySideEntities.map((entity) => ({ value: entity.id, label: entity.entityName }))} nothingFound={entityOptionsHint} w="220px" h="42px" placeholder='Entity (any side)' clearable searchable {...anySideInputProps} onChange={handleAnySideChange} />
                                        <MultiSelect pinSelectedToTop wrapperType="trade" label="My Entity" disabled={anySideSelected} data={myEntities.map((entity) => ({ value: entity.id, label: entity.entityName }))} nothingFound={entityOptionsHint} w="220px" h="42px" placeholder='My Entity' clearable searchable {...form.getInputProps('myEntity')} />
                                        <MultiSelect pinSelectedToTop wrapperType="trade" label="Counterparty" disabled={anySideSelected} data={lenderEntities.map((entity) => ({ value: entity.id, label: entity.entityName }))} nothingFound={entityOptionsHint} w="220px" h="42px" placeholder='Counterparty' searchable clearable {...form.getInputProps('counterparty')} />
                                        <MultiSelect pinSelectedToTop wrapperType="trade" label="Agent" data={agentEntities.map((entity) => ({ value: entity.id, label: entity.entityName }))} nothingFound={entityOptionsHint} w="220px" h="42px" placeholder='Agent' clearable searchable {...form.getInputProps('agent')} />
                                        <MultiSelect pinSelectedToTop wrapperType="trade" label="Deal" data={sortedDeals.map((deal) => ({ value: deal.id, label: deal.dealName }))} nothingFound={dealOptionsHint}
                                            w="220px" h="42px" placeholder='Deal' clearable searchable {...form.getInputProps('deal')} />
                                    </div>
                                    <div className="filterSection">
                                        <div className="filterSectionHeading">Dates</div>
                                        <Group noWrap className="dataEntrySubSection">
                                            <TextLabelInputTradeView label="Trade Date">
                                                <DatePickerInput
                                                    clearable
                                                    w="220px"
                                                    h="42px"
                                                    placeholder='Trade Date'
                                                    valueFormat='DD-MMM-YYYY'
                                                    type="range"
                                                    allowSingleDateInRange={true}
                                                    firstDayOfWeek={0}
                                                    classNames={{
                                                        input: 'newInput',
                                                        wrapper: 'newInputWrapper',
                                                        rightSection: 'customRightSection',
                                                    }}
                                                    styles={(theme) => ({
                                                        ...datePickerStyles(theme),
                                                        input: {
                                                            whiteSpace: 'pre-line',
                                                            textAlign: 'left',
                                                            fontSize: tradeDateDisplay.multiline ? '11px' : '14px',
                                                            lineHeight: tradeDateDisplay.multiline ? 1.15 : 1.5,
                                                        },
                                                    })}
                                                    formattedValue={tradeDateDisplay.text}
                                                    onClear={handleDateClear('tradeDate')}
                                                    rightSection={dateRightSection('tradeDate', !!tradeDateDisplay.text)}
                                                    {...form.getInputProps('tradeDate')}
                                                /></TextLabelInputTradeView>
                                        </Group>
                                        <Group noWrap className="dataEntrySubSection">
                                            <TextLabelInputTradeView label="Settlement Date">
                                                <DatePickerInput
                                                    w="220px"
                                                    h="42px"
                                                    clearable
                                                    placeholder='Settlement Date'
                                                    valueFormat='DD-MMM-YYYY'
                                                    type="range"
                                                    allowSingleDateInRange={true}
                                                    firstDayOfWeek={0}
                                                    classNames={{
                                                        input: 'newInput',
                                                        wrapper: 'newInputWrapper',
                                                        rightSection: 'customRightSection',
                                                    }}
                                                    styles={(theme) => ({
                                                        ...datePickerStyles(theme),
                                                        input: {
                                                            whiteSpace: 'pre-line',
                                                            textAlign: 'left',
                                                            fontSize: settlementDateDisplay.multiline ? '11px' : '14px',
                                                            lineHeight: settlementDateDisplay.multiline ? 1.15 : 1.5,
                                                        },
                                                    })}
                                                    formattedValue={settlementDateDisplay.text}
                                                    onClear={handleDateClear('settlementDate')}
                                                    rightSection={dateRightSection('settlementDate', !!settlementDateDisplay.text)}
                                                    {...form.getInputProps('settlementDate')}
                                                /></TextLabelInputTradeView>
                                        </Group>
                                    </div>
                                </div>
                                <div className="filterMenuRight">
                                    <div className="filterSection">
                                        <div className="filterSectionHeading">Trade Attributes</div>
                                        <MultiSelect wrapperType="trade" label="Trade Action" data={['Buy', 'Sell']} w="220px" h="42px" placeholder='Trade Action' clearable {...form.getInputProps('tradeAction')} />
                                        <MultiSelect wrapperType="trade" label="Payment Method" data={tradePaymentTypeOptions} w="220px" h="42px" placeholder='Payment Method' clearable {...form.getInputProps('paymentMethod')} />
                                        <NewSelectInput wrapperType="trade" label="Allocated" data={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} w="220px" h="42px" placeholder='Allocated' clearable {...form.getInputProps('allocated')} />
                                    </div>
                                    <div className="filterSection">
                                        <div className="filterSectionHeading">Status &amp; Processing</div>
                                        <MultiSelect wrapperType="trade" label="Trade Status" data={tradeStatusOptions} w="220px" h="42px" clearable placeholder='Trade Status' {...form.getInputProps('tradeStatus')} />
                                        <MultiSelect wrapperType="trade" label="AA Status" data={aaStatusOptions} w="220px" h="42px" placeholder='AA Status' clearable {...form.getInputProps('aaStatus')} />
                                        <NewSelectInput wrapperType="trade" label="Hold Status" data={holdStatusOptions} w="220px" h="42px" placeholder='Hold Status' clearable {...form.getInputProps('holdStatus')} />
                                        <NewSelectInput wrapperType="trade" label="Lock Status" data={lockStatusOptions} w="220px" h="42px" placeholder='Lock Status' clearable {...form.getInputProps('lockStatus')} />
                                        <NewSelectInput wrapperType="trade" label="Unapplied Transactions" data={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} w="220px" h="42px" placeholder='Unapplied Transactions' clearable {...form.getInputProps('unappliedTransactions')} />
                                        <NewSelectInput wrapperType="trade" label="Stale Documents" data={staleDocumentsOptions} w="220px" h="42px" placeholder='Stale Documents' clearable {...form.getInputProps('staleDocuments')} />
                                        <NewSelectInput wrapperType="trade" label="Activity Stream" data={activityStreamOptions} w="220px" h="42px" placeholder='Activity Stream' clearable {...form.getInputProps('activityStream')} />
                                        <NewSelectInput wrapperType="trade" label="Documents Not Refreshed" data={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} w="220px" h="42px" placeholder='Documents Not Refreshed' clearable {...form.getInputProps('documentsNotRefreshed')} />
                                    </div>
                                </div>
                            </div>
                            <div className="filterMenuActions">
                                <Button className="clearAllButton" type="button" onClick={clearAllFilters}>
                                    Clear All
                                </Button>
                                <Button className="applyButton" type="submit">
                                    Apply
                                </Button>
                            </div>
                        </div>
                    </form>
                </Popover.Dropdown>
            </Popover>
        </div >
    );
}

export default PlusButtonWithPopover;
